import { Api, type RequestParams } from "@/models/club";
import setAxiosHeader from "@/utilities/helper/setHeader";
import { endSession, getValidAccessToken, hasSession, refreshSession } from "./session";
import { notifications } from "@mantine/notifications";
import { AxiosError, InternalAxiosRequestConfig } from "axios";
import { SUBSCRIPTION_EXPIRED_EVENT } from "./subscriptionEvents";

export const clubApi = new Api({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Headers": "X-Requested-With",
  },
});

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

// Extra request option: skip the global error notification.
export type SilentRequestConfig = { silentErrorToast?: boolean };
export const silentErrors = { silentErrorToast: true } as SilentRequestConfig as RequestParams;

clubApi.instance.interceptors.request.use(
  async (config) => {
    // Refresh before sending instead of collecting a 401 for every request after the token expires.
    await getValidAccessToken();
    return setAxiosHeader(config);
  },
  (error: unknown) => {
    return Promise.reject(error);
  },
);

clubApi.instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error: AxiosError) {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    // Blocked because the subscription ran out: the dashboard opens the renew dialog.
    if ((error?.response?.data as { code?: string })?.code === "SUBSCRIPTION_EXPIRED") window.dispatchEvent(new Event(SUBSCRIPTION_EXPIRED_EVENT));
    const sessionRejected = error?.response?.status === 401 && hasSession();

    // Callers that show the error themselves (review screens) opt out, so it is not shown twice.
    // A 401 for an expired session is handled below and is not the user's concern.
    const silent = (error.config as SilentRequestConfig | undefined)?.silentErrorToast || sessionRejected;
    if (!silent && (error?.response?.data as { message: string })?.message)
      notifications.show({
        message: (error?.response?.data as { message: string }).message,
        color: "red",
      });
    if (!silent && (error?.response?.data as { Message: string })?.Message)
      notifications.show({
        message: (error?.response?.data as { Message: string }).Message,
        color: "red",
      });

    if (sessionRejected && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const rejectedToken = String(originalRequest.headers.get("Authorization") ?? "").replace(/^Bearer /, "");
      try {
        const accessToken = await refreshSession(rejectedToken);
        if (!accessToken) {
          endSession();
          return Promise.reject(error);
        }
        originalRequest.headers.set("Authorization", `Bearer ${accessToken}`);
        return clubApi.instance(originalRequest);
      } catch {
        // Offline or server trouble: keep the session and let only this request fail.
        return Promise.reject(error);
      }
    }

    // Still rejected with a freshly refreshed token.
    if (sessionRejected) endSession();

    return Promise.reject(error);
  },
);

const clubRequest = clubApi.api;

export default clubRequest;
